import axios from "axios";
import { useAxiosHook } from "./useAxiosHook";
import { useDispatch } from "react-redux";
import { cachedDataItemSet } from "../redux/actions/cachedDataActions";
import { CACHED_DATA_REDUCER_TYPES } from "../redux/reducers/cachedDataReducer";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import { useCallback } from "react";
import { isArray, size } from "lodash";

const ROUTE_PREFIX = "borrower_portal_admin";

export const useBorrowerPortalHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const borrowerPortalHookLoading = loading;
  const error = axiosError;
  const dispatch = useDispatch();

  const getBorrowerPortalUsers = async ({
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/all_borrower_portal_users`;
    const source = axios.CancelToken.source();
    const CACHED_DATA_KEY = CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS;

    await axiosGetRequest({
      name: "Get Borrower Portal Users",
      path: endpoint,
      cancelSource: source,
      onSuccessFn: (d) => {
        dispatch(cachedDataItemSet(CACHED_DATA_KEY, d));
        if (onSuccessFn) {
          onSuccessFn(d);
        }
      },
      onCompleteFn,
      onFailFn,
    });
  };

  const getBorrowerProfile = async ({
    _id,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ _id });

    const endpoint = `${ROUTE_PREFIX}/get_borrower_profile/${_id}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Borrower Profile",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateBorrowerProfile = async ({
    _id,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/update_borrower_profile/${_id}`;
    const source = axios.CancelToken.source();

    validateRequiredFields({ data, _id });

    await axiosPostRequest({
      name: "Update Borrower Profile",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn: (d) => {
        if (onSuccessFn) {
          onSuccessFn(d);
        }
      },
      onCompleteFn,
      onFailFn,
    });
  };

  const uploadBorrowerProfileDocuments = async ({
    _id,
    files,
    onSuccessFn,
    onSuccessMsg = "Files Uploaded",
    onFailFn,
  }) => {
    validateRequiredFields({ files, _id });
    const endpoint = `${ROUTE_PREFIX}/upload_borrower_docs/${_id}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Upload Borrower Profile Docs",
      path: endpoint,
      files,
      formData: true,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onFailFn,
    });
  };

  const updateBorrowerDocMetadata = async ({
    borrowerId,
    files,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, files });

    const endpoint = `${ROUTE_PREFIX}/update_borrower_doc_metadata/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Borrower Doc Metadata",
      path: endpoint,
      payload: files,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const getBorrowerProfileDocumentById = useCallback(
    async ({
      borrowerId,
      file_storage_name,
      isFile = true,
      fileDownloadName,
      onSuccessFn,
      fileDownload,
      openInNewTab,
      onFailFn,
    }) => {
      validateRequiredFields({ file_storage_name, borrowerId });
      const endpoint = `${ROUTE_PREFIX}/borrower_file_by_id/${borrowerId}/${file_storage_name}`;
      const source = axios.CancelToken.source();

      await axiosGetRequest({
        name: "Get Borrower Profile Document By Id",
        path: endpoint,
        cancelSource: source,
        isFile,
        fileDownloadName,
        fileDownload,
        openInNewTab,
        onSuccessFn,
        onFailFn,
      });
    },
    [axiosGetRequest],
  );

  const createEntityWithUpload = async ({
    borrowerId,
    files,
    onSuccessFn,
    onSuccessMsg = "Entity Created",
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, files });

    const endpoint = `${ROUTE_PREFIX}/create_entity_with_upload/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Entity With Upload",
      path: endpoint,
      files,
      formData: true,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onFailFn,
    });
  };

  const createEntity = async ({
    borrowerId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, data });

    const endpoint = `${ROUTE_PREFIX}/create_entity/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Entity",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Entity added successfully",
      onFailFn,
    });
  };

  const createEntityWithOrWithoutFiles = async ({
    borrowerId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, data });
    const endpoint_FILES = `${ROUTE_PREFIX}/create_entity_with_upload/${borrowerId}`;
    const endpoint_NO_FILES = `${ROUTE_PREFIX}/create_entity/${borrowerId}`;
    const source = axios.CancelToken.source();

    if (isArray(data?.uploadMetadata) && size(data?.uploadMetadata) >= 1) {
      // console.log("With Files", data);
      // return;
      await axiosPostRequest({
        name: "Create Entity With Files",
        payload: data,
        path: endpoint_FILES,
        formData: true,
        cancelSource: source,
        onSuccessFn,
        onSuccessMsg: "Entity added successfully",
        onCompleteFn,
        onFailFn,
      });
    } else {
      // console.log("Without Files", data);
      // return;
      await axiosPostRequest({
        name: "Create Entity Without Files",
        payload: data,
        path: endpoint_NO_FILES,
        cancelSource: source,
        onSuccessMsg: "Entity added successfully",
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    }
  };

  const updateEntity = async ({
    borrowerId,
    entityId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, entityId, data });

    const endpoint = `${ROUTE_PREFIX}/update_entity/${borrowerId}/${entityId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Entity",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Entity updated successfully",
      onFailFn,
    });
  };

  const createExperienceRecord = async ({
    borrowerId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, data });

    const endpoint = `${ROUTE_PREFIX}/create_experience_record/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Experience Record",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Experience record created successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const updateExperienceRecord = async ({
    borrowerId,
    recordId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, recordId, data });

    const endpoint = `${ROUTE_PREFIX}/update_experience_record/${borrowerId}/${recordId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Experience Record",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Experience record updated successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const addBankAccount = async ({
    borrowerId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, data });

    const endpoint = `${ROUTE_PREFIX}/add_bank_account/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Add Bank Account",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Bank account added successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const updateBankAccount = async ({
    borrowerId,
    _id,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, _id, data });

    const endpoint = `${ROUTE_PREFIX}/update_bank_account/${borrowerId}/${_id}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Bank Account",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Bank account updated successfully",
      onFailFn,
    });
  };

  const searchBorrowerByEmail = async ({
    email,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ email });

    const endpoint = `${ROUTE_PREFIX}/search_user_by_email/${email}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Search Borrower Profile By Email",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createBorrowerProfileStaging = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ data });

    const endpoint = `${ROUTE_PREFIX}/create_borrower_profile_staging`;
    const source = axios.CancelToken.source();

    const EX_RETURN = STAGED_USER;

    // onSuccessFn(STAGED_USER);
    // console.log("createBorrowerProfileStaging data", { data });
    // return;
    await axiosPostRequest({
      name: "Create Borrower Profile Staging",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Email Sent to Borrower",
      onFailFn,
    });
  };

  const getStagedBorrowerByEmail = async ({
    email,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ email });

    const endpoint = `${ROUTE_PREFIX}/get_staged_borrower_profile_by_email/${email}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get staged Borrower By Email",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const getAllStagedBorrowers = async ({
    salespersonEmail,
    activeStatus,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/get_all_staged_borrower_profiles`;
    const source = axios.CancelToken.source();
    const params = new URLSearchParams();
    if (salespersonEmail) params.append("salespersonEmail", salespersonEmail);
    if (activeStatus) params.append("activeStatus", activeStatus);
    const queryString = params.toString();

    await axiosGetRequest({
      name: "Get All Staged Borrowers",
      path: queryString ? `${endpoint}?${queryString}` : endpoint,
      cancelSource: source,
      onSuccessFn: (d) => {
        dispatch(
          cachedDataItemSet(
            CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS_STAGED,
            d,
          ),
        );
        if (onSuccessFn) {
          onSuccessFn(d);
        }
      },
      onCompleteFn,
      onFailFn,
    });
  };

  const createBorrowerProfileFromStaging = async ({
    emailAddress,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ emailAddress });

    const endpoint = `${ROUTE_PREFIX}/create_borrower_profile_from_staging`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Borrower Profile From Staging",
      path: endpoint,
      payload: { emailAddress },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Borrower profile created from staging",
      onCompleteFn,
      onFailFn,
    });
  };

  const resendBorrowerProfileInvite = async ({
    emailAddress,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ emailAddress });

    const endpoint = `${ROUTE_PREFIX}/resend_borrower_profile_invite`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Resend Borrower Profile Invite",
      path: endpoint,
      payload: { emailAddress },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Invite resent successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const startStagedApplicationAndInvite = async ({
    emailAddress,
    applicationData,
    sendInvite = true,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ emailAddress, applicationData });

    const endpoint = `${ROUTE_PREFIX}/start_staged_application_and_invite`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Start Staged Application And Invite",
      path: endpoint,
      payload: {
        emailAddress,
        applicationData,
        sendInvite,
        ...data,
      },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Staged application created",
      onCompleteFn,
      onFailFn,
    });
  };

  const deleteBorrowerUserAccount = async ({
    emailAddress,

    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ emailAddress });

    const endpoint = `${ROUTE_PREFIX}/delete_borrower_user_account`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Delete Borrower User Account",
      path: endpoint,
      payload: {
        emailAddress,
      },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Borrower user account deleted successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const deleteStagedBorrowerUserAccount = async ({
    emailAddress,

    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ emailAddress });

    const endpoint = `${ROUTE_PREFIX}/delete_staged_borrower_user`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Delete Staged Borrower User Account",
      path: endpoint,
      payload: {
        emailAddress,
      },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Staged borrower user account deleted successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  return {
    borrowerPortalHookLoading,
    loading,
    error,

    getBorrowerPortalUsers,
    getBorrowerProfile,
    updateBorrowerProfile,
    uploadBorrowerProfileDocuments,
    updateBorrowerDocMetadata,
    getBorrowerProfileDocumentById,
    createEntityWithUpload,
    createEntity,
    createEntityWithOrWithoutFiles,
    updateEntity,
    createExperienceRecord,
    updateExperienceRecord,
    addBankAccount,
    updateBankAccount,
    searchBorrowerByEmail,
    getStagedBorrowerByEmail,
    createBorrowerProfileStaging,
    getAllStagedBorrowers,
    createBorrowerProfileFromStaging,
    resendBorrowerProfileInvite,
    startStagedApplicationAndInvite,
    deleteBorrowerUserAccount,
    deleteStagedBorrowerUserAccount,
  };
};

const STAGED_USER = {
  _id: "69bdaf01c8b1d89a88f8cb99",
  activeStatus: "inactive",
  convertedDate: null,
  createdBy: "dev2@arrowservicing.com",
  createdDate: "2026-03-20T20:33:05.641000",
  email_domain: "gmail.com",
  emailAddress: "jolivacliffcomortgage@gmail.com",
  firstName: "JamesCC",
  firstSignInDate: null,
  hasSignedIn: false,
  invite_sent: false,
  invite_sent_date: null,
  invitedBy: null,
  inviteSent: false,
  isStaged: true,
  lastName: "OlivaCC",
  lastSignInDate: null,
  permittedUsers: ["dev2@arrowservicing.com"],
  phone: "6314563372",
  staged_user: true,
  stagedApplicationCount: 0,
  stagedApplications: [],
  stagedDate: "2026-03-20T20:33:05.641000",
  staging_user: {
    _id: "69209d06e28939fa8dfffaa0",
    company: "Lendmarq",
    createdDate: "2025-11-21T12:10:30.734000",
    emailAddress: "dev2@arrowservicing.com",
    emailSignature: " ",
    firstName: "Jimmy",
    fullName: "Jimmy Oliva",
    lastName: "Oliva",
    member_id: "member-live-1b0b5c95-55b7-47d5-82e7-385a6b3bfb92",
    organization_id: "organization-live-e345fd6c-502a-44a5-ab4c-cf836d816e10",
    permissions: ["admin"],
    phone: "6314563373",
    phone_number: null,
    roles: ["admin"],
    teams: [],
    testData: {
      testNumber: 3,
    },
    trusted_metadata: {
      emailAddress: "dev2@arrowservicing.com",
      firstName: "Jimmy",
      lastName: "Oliva",
    },
    user_project_type: "b2b",
  },
  stagingStatus: "staged",
  trusted_metadata: {
    emailAddress: "jolivacliffcomortgage@gmail.com",
    firstName: "JamesCC",
    lastName: "OlivaCC",
  },
  user_existed: false,
};

const EX_RES__CREATE_WITH_LOAN_APP = {
  message: "Staged application created",
  staged_user: {
    _id: "69bdb5aac8b1d89a88f8cd36",
    emailAddress: "ajoliva630@gmail.com",
    firstName: "AJ",
    lastName: "Oliva",
    phone: "6314125484",
    trusted_metadata: {
      emailAddress: "ajoliva630@gmail.com",
      firstName: "AJ",
      lastName: "Oliva",
    },
    createdDate: "2026-03-20T21:01:30.150000",
    stagedDate: "2026-03-20T21:01:30.150000",
    user_existed: false,
    createdBy: "dev2@arrowservicing.com",
    staging_user: {
      _id: "69209d06e28939fa8dfffaa0",
      firstName: "Jimmy",
      lastName: "Oliva",
      organization_id: "organization-live-e345fd6c-502a-44a5-ab4c-cf836d816e10",
      user_project_type: "b2b",
      emailAddress: "dev2@arrowservicing.com",
      phone_number: null,
      member_id: "member-live-1b0b5c95-55b7-47d5-82e7-385a6b3bfb92",
      roles: ["admin"],
      trusted_metadata: {
        emailAddress: "dev2@arrowservicing.com",
        firstName: "Jimmy",
        lastName: "Oliva",
      },
      permissions: ["admin"],
      teams: [],
      fullName: "Jimmy Oliva",
      createdDate: "2025-11-21T12:10:30.734000",
      testData: {
        testNumber: 3,
      },

      emailSignature: " ",
      company: "Lendmarq",
      phone: "6314563373",
    },
    invite_sent: false,
    invite_sent_date: null,
    firstSignInDate: null,
    lastSignInDate: null,
    stagingStatus: "staged",
    invitedBy: null,
    convertedDate: null,
    permittedUsers: ["dev2@arrowservicing.com"],
    stagedApplications: [
      {
        subjectProperties: [{}],
        borrowers: [
          {
            emailAddress: "ajoliva630@gmail.com",
            firstName: "AJ",
            lastName: "Oliva",
            phone: "6314125484",
          },
        ],
        loanProductType: "Fix and Flip",
        loanPurpose: "Purchase",
        exitStrategy: "Sell",
        expectedClosingDate: "2026-04-09T00:00:00.000Z",
        baseLoanAmount: 985600,
        _id: "69bdb5aac8b1d89a88f8cd37",
        createdDate: "2026-03-20T21:01:30.166000",
        createdBy: "dev2@arrowservicing.com",
        stagedDate: "2026-03-20T21:01:30.166000",
        applicationStatus: "staged",
        startedByAdmin: true,
        startedByUser: false,
      },
    ],
    staged_user: true,
    email_domain: "gmail.com",
    hasSignedIn: false,
    activeStatus: "inactive",
    inviteSent: false,
    isStaged: true,
    stagedApplicationCount: 1,
  },
  invite_response: {
    staged_user: {
      _id: "69bdb5aac8b1d89a88f8cd36",
      emailAddress: "ajoliva630@gmail.com",
      firstName: "AJ",
      lastName: "Oliva",
      phone: "6314125484",
      trusted_metadata: {
        emailAddress: "ajoliva630@gmail.com",
        firstName: "AJ",
        lastName: "Oliva",
      },
      createdDate: "2026-03-20T21:01:30.150000",
      stagedDate: "2026-03-20T21:01:30.150000",
      user_existed: false,
      createdBy: "dev2@arrowservicing.com",
      staging_user: {
        _id: "69209d06e28939fa8dfffaa0",
        firstName: "Jimmy",
        lastName: "Oliva",
        organization_id:
          "organization-live-e345fd6c-502a-44a5-ab4c-cf836d816e10",
        user_project_type: "b2b",
        emailAddress: "dev2@arrowservicing.com",
        phone_number: null,
        member_id: "member-live-1b0b5c95-55b7-47d5-82e7-385a6b3bfb92",
        roles: ["admin"],
        trusted_metadata: {
          emailAddress: "dev2@arrowservicing.com",
          firstName: "Jimmy",
          lastName: "Oliva",
        },
        permissions: ["admin"],
        teams: [],
        fullName: "Jimmy Oliva",
        createdDate: "2025-11-21T12:10:30.734000",
        testData: {
          testNumber: 3,
        },

        emailSignature: " ",
        company: "Lendmarq",
        phone: "6314563373",
      },
      invite_sent: true,
      invite_sent_date: "2026-03-20T21:01:30.178000",
      firstSignInDate: null,
      lastSignInDate: null,
      stagingStatus: "invited",
      invitedBy: "dev2@arrowservicing.com",
      convertedDate: "2026-03-20T21:01:30.178000",
      permittedUsers: ["dev2@arrowservicing.com"],
      stagedApplications: [
        {
          subjectProperties: [{}],
          borrowers: [
            {
              emailAddress: "ajoliva630@gmail.com",
              firstName: "AJ",
              lastName: "Oliva",
              phone: "6314125484",
            },
          ],
          loanProductType: "Fix and Flip",
          loanPurpose: "Purchase",
          exitStrategy: "Sell",
          expectedClosingDate: "2026-04-09T00:00:00.000Z",
          baseLoanAmount: 985600,
          _id: "69bdb5aac8b1d89a88f8cd37",
          createdDate: "2026-03-20T21:01:30.166000",
          createdBy: "dev2@arrowservicing.com",
          stagedDate: "2026-03-20T21:01:30.166000",
          applicationStatus: "staged",
          startedByAdmin: true,
          startedByUser: false,
        },
      ],
      staged_user: true,
      email_domain: "gmail.com",
      hasSignedIn: false,
      activeStatus: "inactive",
      inviteSent: true,
      isStaged: true,
      stagedApplicationCount: 1,
    },
    live_user: {
      _id: "69bdb5aac8b1d89a88f8cd36",
      firstName: "AJ",
      lastName: "Oliva",
      phone: "6314125484",
      emailAddress: "ajoliva630@gmail.com",
      createDateTime: "2026-03-19T20:17:56.380000",
      signUpDate: "2026-03-19T20:17:56.380000",
      session_duration_minutes: 4320,
      create_user_session_minutes: 720,
      user_project_type: "borrower_portal",
      applications: [],
      creditAuths: [],
      creditPulls: [],
      backgroundReports: [],
      dealIds: [],
      subjectProperties: [],
      preapprovals: [],
      loanQuotes: [],
      appraisalOrders: [],
      propertyBids: [],
      borrowerComments: [],
      borrowerDocuments: [],
      parsedDocuments: [],
      parsedId: {},
      associatedEntities: [],
      validatedExperience: [],
      staged_user: false,
      invite_sent: true,
      invite_sent_date: "2026-03-20T21:01:30.178000",
      stagingStatus: "invited",
      invitedBy: "dev2@arrowservicing.com",
      stagedDate: "2026-03-20T21:01:30.150000",
      convertedDate: "2026-03-20T21:01:30.178000",
      permittedUsers: ["dev2@arrowservicing.com"],
      stagedApplications: [
        {
          subjectProperties: [{}],
          borrowers: [
            {
              emailAddress: "ajoliva630@gmail.com",
              firstName: "AJ",
              lastName: "Oliva",
              phone: "6314125484",
            },
          ],
          loanProductType: "Fix and Flip",
          loanPurpose: "Purchase",
          exitStrategy: "Sell",
          expectedClosingDate: "2026-04-09T00:00:00.000Z",
          baseLoanAmount: 985600,
          _id: "69bdb5aac8b1d89a88f8cd37",
          createdDate: "2026-03-20T21:01:30.166000",
          createdBy: "dev2@arrowservicing.com",
          stagedDate: "2026-03-20T21:01:30.166000",
          applicationStatus: "staged",
          startedByAdmin: true,
          startedByUser: false,
        },
      ],
      trusted_metadata: {
        emailAddress: "ajoliva630@gmail.com",
        firstName: "AJ",
        lastName: "Oliva",
        userId: "69bdb5aac8b1d89a88f8cd36",
      },
      createdDate: "2026-03-20T21:01:31.677000",
      user_existed: false,
      createdBy: "dev2@arrowservicing.com",
      staging_user: {
        _id: "69209d06e28939fa8dfffaa0",
        firstName: "Jimmy",
        lastName: "Oliva",
        organization_id:
          "organization-live-e345fd6c-502a-44a5-ab4c-cf836d816e10",
        user_project_type: "b2b",
        emailAddress: "dev2@arrowservicing.com",
        phone_number: null,
        member_id: "member-live-1b0b5c95-55b7-47d5-82e7-385a6b3bfb92",
        roles: ["admin"],
        trusted_metadata: {
          emailAddress: "dev2@arrowservicing.com",
          firstName: "Jimmy",
          lastName: "Oliva",
        },
        permissions: ["admin"],
        teams: [],
        fullName: "Jimmy Oliva",
        createdDate: "2025-11-21T12:10:30.734000",
        testData: {
          testNumber: 3,
        },

        emailSignature: " ",
        company: "Lendmarq",
        phone: "6314563373",
      },
      email_domain: "gmail.com",
      hasSignedIn: false,
      activeStatus: "inactive",
      inviteSent: true,
      isStaged: false,
      stagedApplicationCount: 1,
      profileComplete: true,
      email_lower: "ajoliva630@gmail.com",
      fullName: "AJ Oliva",
      stytch_user_id: "user-live-d736ca73-4322-4796-bab5-fd0ffa695520",
    },
    stytch_user: {
      user_id: "user-live-d736ca73-4322-4796-bab5-fd0ffa695520",
      emails: [
        {
          email_id: "email-live-42b7e282-df1a-4acb-8b91-1c97dd1e3b4f",
          email: "ajoliva630@gmail.com",
          verified: false,
        },
      ],
      status: "active",
      phone_numbers: [],
      webauthn_registrations: [],
      providers: [],
      totps: [],
      crypto_wallets: [],
      biometric_registrations: [],
      name: {
        first_name: "AJ",
        middle_name: "",
        last_name: "Oliva",
      },
      created_at: "2026-03-20T21:01:31+00:00",
      password: null,
      trusted_metadata: {
        emailAddress: "ajoliva630@gmail.com",
        firstName: "AJ",
        lastName: "Oliva",
        userId: "69bdb5aac8b1d89a88f8cd36",
      },
      untrusted_metadata: {},
    },
  },
};
